/*
 * Sample report data (P8). The counts are this quarter's, company-wide, and
 * agree with the dashboard (240 prospects → 118 leads → 64 opportunities →
 * 22 won). Where a figure can be worked out from the other sample data
 * (customers, prospects) the report screens compute it instead.
 */
import { daysFromNow } from "@/helpers/crm";
import { prospects } from "@/mock/prospects";
import { tasks, taskStatus } from "@/mock/work";
import { staffName } from "@/mock/masters";

/*
 * Report catalogue, exactly the P8 list. `route` is the dedicated screen
 * where one exists; the rest open a preview of the report's columns.
 */
export const reportTiers = ["MVP", "Phase 2", "Advanced"];

const R = (key, tier, name, description, module, route = null, extra = {}) => ({ key, tier, name, description, module, route, formats: ["Excel", "PDF"], ...extra });

export const reportCatalogue = [
  R("prospects", "MVP", "Prospect report", "Prospects by stage, owner, source and industry, with age and last contact.", "Prospects", null),
  R("leads", "MVP", "Lead report", "Leads by stage and qualification completeness; conversions to opportunity.", "Leads", null),
  R("pipeline", "MVP", "Pipeline report", "Open opportunities by stage, value, owner and expected close.", "Opportunities", "reports.sales"),
  R("won_lost", "MVP", "Won / lost report", "Deals closed in the period, with lost reason and competitor.", "Opportunities", "reports.sales"),
  R("customer_register", "MVP", "Customer register", "All customers with lifecycle, products, MRR, owner and renewal date.", "Customers", "reports.customers"),
  R("onboarding", "MVP", "Onboarding progress", "Implementations by milestone, delay and planned vs actual go-live.", "Onboarding", "reports.customers"),
  R("tickets", "MVP", "Support tickets", "Tickets by client, channel, product, priority and SLA status.", "Support", "reports.support"),
  R("complaints", "MVP", "Complaints", "Complaints by category and root cause, with resolution time.", "Support", "reports.support"),
  R("renewals", "MVP", "Renewals", "Subscriptions due for renewal by month, value, probability and owner.", "Renewals", "reports.customers"),
  R("churn_exit", "MVP", "Churn / exit report", "Customers that left or are leaving, with exit reason and value.", "Retention", "reports.customers"),
  R("overdue_tasks", "MVP", "Overdue tasks", "Open tasks and follow-ups past due, by owner and escalation level.", "Activities", null),
  R("lead_source", "Phase 2", "Lead source performance", "Volume, conversion and won value by lead source and campaign.", "Leads", "reports.sales"),
  R("pipeline_ageing", "Phase 2", "Pipeline ageing", "How long open deals have been in each stage against the stage limit.", "Opportunities", "reports.sales"),
  R("salesperson", "Phase 2", "Salesperson performance", "Calls, demos, proposals, wins, win rate, average deal and cycle time.", "Sales", "reports.sales"),
  R("product_conversion", "Phase 2", "Product conversion", "Win rate and value by product and package.", "Opportunities", "reports.sales"),
  R("health", "Phase 2", "Customer health", "Health distribution and the indicators driving it.", "Customer Success", "reports.customers"),
  R("adoption", "Phase 2", "Adoption", "Active vs licensed users and inactive users by customer.", "Customer Success", "reports.customers"),
  R("csat", "Phase 2", "CSAT", "Satisfaction scores by month, customer and ticket.", "Customer Success", "reports.customers"),
  R("nps", "Phase 2", "NPS", "Promoters, passives and detractors with trend.", "Customer Success", "reports.customers"),
  R("upsell", "Phase 2", "Upsell / cross-sell", "Expansion opportunities and value won from existing customers.", "Customer Success", "reports.customers"),
  R("churn_analysis", "Phase 2", "Churn analysis", "Churn rate by product, industry, segment and reason; recovered and win-back.", "Retention", "reports.customers"),
  R("kpi_catalogue", "Advanced", "KPI catalogue", "Every KPI with its formula, source, owner, frequency and target.", "Reports", "reports.kpis"),
  R("formula_engine", "Advanced", "Formula engine", "Tenant-configurable KPI formulas with a change log.", "Reports", "reports.kpis"),
  R("executive", "Advanced", "Executive dashboard", "One page for management: pipeline, revenue, customers, risk.", "Reports", "dashboards.home"),
  R("forecast", "Advanced", "Sales / renewal forecast", "Weighted pipeline and renewal forecast against target.", "Sales", "reports.sales"),
  R("revenue_at_risk", "Advanced", "Revenue at risk", "ARR of customers at risk or critical, by reason and owner.", "Retention", "reports.customers"),
  R("drilldown", "Advanced", "Drill-down", "Company → team → salesperson → customer for every funnel figure.", "Reports", "reports.funnel"),
  R("snapshots", "Advanced", "Historical snapshots", "Locked month-end figures that no later edit can change.", "Reports", "reports.kpis"),
];

/** Sample rows for the reports without their own screen. */
export const reportPreview = (key) => {
  if (key === "prospects")
    return {
      columns: ["Code", "Organisation", "Stage", "Owner", "Source", "Industry", "Age (days)"],
      rows: prospects.slice(0, 12).map((p) => [p.code, p.organisation, p.stage, p.owner_id ? staffName(p.owner_id) : "Pool", p.source, p.industry, Math.round((Date.now() - new Date(p.created_at)) / 864e5)]),
    };
  if (key === "leads")
    return {
      columns: ["Code", "Organisation", "Stage", "Owner", "Qualification", "Created"],
      rows: prospects
        .filter((p) => ["qualified", "converted"].includes(p.stage))
        .map((p, i) => [`LED-${String(40 + p.id).padStart(4, "0")}`, p.organisation, ["New", "Contacted", "Qualified"][i % 3], staffName(p.owner_id), `${3 + (i % 3)}/5`, p.created_at]),
    };
  if (key === "overdue_tasks")
    return {
      columns: ["Task", "Type", "Related", "Owner", "Due", "Escalated to"],
      rows: tasks.filter((t) => taskStatus(t) === "overdue").map((t) => [t.title, t.type, t.related?.label ?? "—", staffName(t.owner_id), t.due, t.escalated?.to ?? "—"]),
    };
  return { columns: ["Item", "Value"], rows: [["Sample", "—"]] };
};

/* ---------- Conversion & drop-off (reports.funnel) ---------- */

export const journeySteps = [
  { key: "prospect", name: "Prospect", count: 240 },
  { key: "lead", name: "Lead", count: 118 },
  { key: "opportunity", name: "Opportunity", count: 64 },
  { key: "won", name: "Won", count: 22 },
  { key: "onboarded", name: "Onboarded", count: 21 },
  { key: "live", name: "Live", count: 19 },
  { key: "renewed", name: "Renewed", count: 16 },
];

// Where records fell out (row) and why (column = reason category key).
export const dropOffMatrix = [
  { stage: "Prospect", fit: 14, price: 11, timing: 12, competitor: 0, contact: 38, solution: 9, product: 0, data: 10, service: 0, business: 0, other: 2 },
  { stage: "Lead", fit: 6, price: 13, timing: 8, competitor: 0, contact: 5, solution: 4, product: 3, data: 1, service: 0, business: 0, other: 1 },
  { stage: "Opportunity", fit: 2, price: 7, timing: 3, competitor: 11, contact: 1, solution: 1, product: 3, data: 0, service: 0, business: 0, other: 0 },
  { stage: "Won → Onboarded", fit: 0, price: 0, timing: 0, competitor: 0, contact: 0, solution: 0, product: 0, data: 0, service: 1, business: 0, other: 0 },
  { stage: "Onboarded → Live", fit: 0, price: 0, timing: 1, competitor: 0, contact: 0, solution: 0, product: 0, data: 0, service: 1, business: 0, other: 0 },
  { stage: "Live → Renewed", fit: 0, price: 1, timing: 0, competitor: 1, contact: 0, solution: 0, product: 0, data: 0, service: 0, business: 1, other: 0 },
];

// Preventable reason categories per the reason-code flags (Settings → Reason Codes).
export const preventableSplit = [
  { stage: "Prospect", preventable: 41, unavoidable: 55 },
  { stage: "Lead", preventable: 17, unavoidable: 24 },
  { stage: "Opportunity", preventable: 19, unavoidable: 9 },
  { stage: "Onboarding", preventable: 3, unavoidable: 0 },
  { stage: "Renewal", preventable: 2, unavoidable: 1 },
];

// [dimension value, entered, reached lead, reached opportunity, won, top drop-off reason]
export const dropOffBy = {
  source: [
    ["Website", 52, 27, 15, 5, "CON-01"],
    ["Referral", 31, 22, 14, 7, "PRC-01"],
    ["Google Ads", 44, 15, 7, 2, "DAT-02"],
    ["LinkedIn", 28, 13, 6, 2, "CON-01"],
    ["Event", 26, 14, 8, 2, "TIM-01"],
    ["Outbound call", 30, 11, 5, 1, "CON-01"],
    ["Agent / Partner", 14, 9, 6, 2, "CMP-01"],
    ["Existing customer", 6, 5, 3, 1, "PRC-02"],
    ["Walk-in", 9, 2, 0, 0, "FIT-01"],
  ],
  owner: [
    ["Jane Wanjiku", 62, 32, 18, 7, "CON-01"],
    ["Brian Otieno", 38, 21, 12, 5, "CMP-01"],
    ["Ann Mutua", 54, 24, 13, 4, "PRC-01"],
    ["Lucy Chebet", 46, 22, 11, 3, "CON-01"],
    ["Peter Ouma", 40, 19, 10, 3, "CON-02"],
  ],
  industry: [
    ["Manufacturing", 38, 21, 12, 4, "PRC-01"],
    ["Healthcare", 34, 15, 8, 3, "CON-02"],
    ["Hospitality", 29, 14, 9, 3, "CMP-01"],
    ["SACCO", 26, 16, 10, 4, "TIM-02"],
    ["Retail", 31, 13, 7, 2, "PRC-02"],
    ["Education", 27, 12, 6, 2, "PRC-01"],
    ["Logistics", 22, 12, 6, 2, "CON-01"],
    ["Agriculture", 18, 8, 3, 1, "FIT-01"],
    ["Other", 15, 7, 3, 1, "SOL-01"],
  ],
  segment: [
    ["SME", 98, 38, 17, 5, "FIT-01"],
    ["Mid-market", 96, 52, 30, 11, "PRC-01"],
    ["Enterprise", 39, 23, 14, 5, "CMP-01"],
    ["Strategic", 7, 5, 3, 1, "TIM-02"],
  ],
  territory: [
    ["Nairobi Enterprise", 118, 63, 36, 13, "CMP-01"],
    ["Coast", 41, 19, 10, 3, "CON-01"],
    ["Western", 29, 13, 7, 2, "CON-02"],
    ["Rift", 34, 15, 7, 3, "PRC-01"],
    ["Uganda", 18, 8, 4, 1, "PRC-02"],
  ],
};

// Average days spent in each step for records that moved on, against the stage limit.
export const daysPerStage = [
  { stage: "Prospect → Lead", avg: 18, limit: 21 },
  { stage: "Lead → Opportunity", avg: 12, limit: 14 },
  { stage: "Qualified", avg: 6, limit: 7 },
  { stage: "Discovery", avg: 15, limit: 14 },
  { stage: "Demo", avg: 11, limit: 14 },
  { stage: "Proposal", avg: 24, limit: 21 },
  { stage: "Negotiation", avg: 17, limit: 21 },
  { stage: "Contract", avg: 9, limit: 14 },
  { stage: "Won → Live", avg: 46, limit: 60 },
];

// Prospects recycled to the pool after disqualification and what happened next.
export const recycle = {
  recycled: 38,
  reworked: 29,
  converted_second: 7,
  by_reason: [
    { code: "TIM-01", recycled: 12, converted: 3 },
    { code: "PRC-01", recycled: 9, converted: 2 },
    { code: "CON-01", recycled: 8, converted: 1 },
    { code: "SOL-01", recycled: 5, converted: 1 },
    { code: "FIT-01", recycled: 4, converted: 0 },
  ],
};

// Drill-down: company → team → salesperson (prospects, leads, opps, won, won value).
export const funnelByPerson = [
  { staff_id: 1, team_id: 1, prospects: 62, leads: 32, opps: 18, won: 7, value: 6900000 },
  { staff_id: 2, team_id: 1, prospects: 38, leads: 21, opps: 12, won: 5, value: 5800000 },
  { staff_id: 3, team_id: 2, prospects: 54, leads: 24, opps: 13, won: 4, value: 2100000 },
  { staff_id: 5, team_id: 2, prospects: 46, leads: 22, opps: 11, won: 3, value: 1500000 },
  { staff_id: 4, team_id: 3, prospects: 40, leads: 19, opps: 10, won: 3, value: 2300000 },
];

/* ---------- Sales (reports.sales) ---------- */

export const pipelineByStage = [
  { key: "qualified", count: 6, value: 3100000 },
  { key: "discovery", count: 7, value: 6400000 },
  { key: "demo", count: 6, value: 8200000 },
  { key: "proposal", count: 7, value: 9900000 },
  { key: "negotiation", count: 4, value: 7300000 },
  { key: "contract", count: 4, value: 6300000 },
];

export const wonLostTrend = {
  months: ["May", "Jun", "Jul", "Aug", "Sep", "Oct"],
  won: [5, 6, 4, 7, 8, 7],
  lost: [9, 7, 10, 8, 9, 11],
  won_value: [3.8, 4.6, 2.9, 5.1, 6.0, 6.3],
};

// Open deals by stage and how long they have been there (days).
export const pipelineAgeing = [
  { stage: "Qualified", b0: 4, b1: 2, b2: 0, b3: 0 },
  { stage: "Discovery", b0: 3, b1: 2, b2: 2, b3: 0 },
  { stage: "Demo", b0: 3, b1: 2, b2: 1, b3: 0 },
  { stage: "Proposal", b0: 2, b1: 2, b2: 2, b3: 1 },
  { stage: "Negotiation", b0: 1, b1: 2, b2: 1, b3: 0 },
  { stage: "Contract", b0: 2, b1: 1, b2: 0, b3: 1 },
];

export const scorecard = [
  { staff_id: 1, calls: 142, demos: 14, proposals: 11, wins: 7, losses: 9, avg_deal: 985000, cycle: 54, target: 7500000 },
  { staff_id: 2, calls: 88, demos: 10, proposals: 8, wins: 5, losses: 4, avg_deal: 1160000, cycle: 61, target: 6000000 },
  { staff_id: 3, calls: 164, demos: 12, proposals: 9, wins: 4, losses: 7, avg_deal: 525000, cycle: 41, target: 3000000 },
  { staff_id: 5, calls: 151, demos: 9, proposals: 7, wins: 3, losses: 6, avg_deal: 500000, cycle: 38, target: 2500000 },
  { staff_id: 4, calls: 119, demos: 8, proposals: 6, wins: 3, losses: 5, avg_deal: 766000, cycle: 57, target: 3500000 },
];

export const productConversion = [
  { product_id: 1, opps: 29, won: 11, lost: 12, value: 9800000 },
  { product_id: 2, opps: 11, won: 4, lost: 4, value: 5600000 },
];

// Next three months: weighted pipeline, best case and commit against target (KES).
export const forecast = [
  { month: "Nov", target: 7000000, commit: 4200000, weighted: 6100000, best: 9800000 },
  { month: "Dec", target: 7000000, commit: 2600000, weighted: 5400000, best: 11200000 },
  { month: "Jan", target: 8000000, commit: 900000, weighted: 4300000, best: 10300000 },
];

export const coverage = [
  { team_id: 1, pipeline: 22400000, target: 13500000 },
  { team_id: 2, pipeline: 9800000, target: 5500000 },
  { team_id: 3, pipeline: 9000000, target: 3500000 },
];

/* ---------- Customers (reports.customers) ---------- */

export const csatNpsTrend = {
  months: ["May", "Jun", "Jul", "Aug", "Sep", "Oct"],
  csat: [84, 86, 82, 85, 81, 79],
  nps: [32, 35, 30, 34, 28, 26],
};

export const npsBreakdown = { promoters: 46, passives: 34, detractors: 20, responses: 118 };

export const expansion = [
  { customer_id: 2, type: "Upsell", product_id: 2, value: 540000, stage: "Won", owner_id: 2 },
  { customer_id: 9, type: "Cross-sell", product_id: 1, value: 720000, stage: "Negotiation", owner_id: 2 },
  { customer_id: 13, type: "Upsell", product_id: 1, value: 360000, stage: "Negotiation", owner_id: 1 },
  { customer_id: 10, type: "Upsell", product_id: 1, value: 936000, stage: "Demo", owner_id: 5 },
  { customer_id: 18, type: "Cross-sell", product_id: 2, value: 1020000, stage: "Discovery", owner_id: 4 },
  { customer_id: 19, type: "Cross-sell", product_id: 1, value: 270000, stage: "Won", owner_id: 2 },
];

// Last 12 months.
export const churn = {
  opening_customers: 152,
  churned: 9,
  churn_value: 3840000,
  recovered: 3,
  recovered_value: 1260000,
  win_back_attempts: 7,
  win_backs: 2,
  by_reason: [
    { code: "CMP-01", count: 3, value: 1560000 },
    { code: "SRV-01", count: 2, value: 720000 },
    { code: "PRC-02", count: 2, value: 600000 },
    { code: "BUS-01", count: 1, value: 300000 },
    { code: "PRD-01", count: 1, value: 660000 },
  ],
  by_product: [
    { product_id: 1, count: 7, value: 2760000 },
    { product_id: 2, count: 2, value: 1080000 },
  ],
  by_industry: [["Hospitality", 3], ["Healthcare", 2], ["Retail", 2], ["NGO", 1], ["Logistics", 1]],
  by_segment: [["SME", 5], ["Mid-market", 3], ["Enterprise", 1]],
};

// Onboarding (customers in onboarding now + recently live) with days to go live.
export const onboardingProgress = [
  { customer_id: 7, progress: 72, planned: daysFromNow(-9), actual: null, started: daysFromNow(-40), status: "Delayed", risk: "high" },
  { customer_id: 8, progress: 55, planned: daysFromNow(14), actual: null, started: daysFromNow(-25), status: "On track", risk: "low" },
  { customer_id: 11, progress: 38, planned: daysFromNow(9), actual: null, started: daysFromNow(-15), status: "At risk", risk: "medium" },
  { customer_id: 13, progress: 100, planned: daysFromNow(-100), actual: daysFromNow(-96), started: daysFromNow(-150), status: "Live", risk: "low" },
  { customer_id: 16, progress: 100, planned: daysFromNow(-150), actual: daysFromNow(-140), started: daysFromNow(-190), status: "Live", risk: "low" },
  { customer_id: 10, progress: 100, planned: daysFromNow(-180), actual: daysFromNow(-172), started: daysFromNow(-220), status: "Live", risk: "low" },
  { customer_id: 20, progress: 47, planned: daysFromNow(38), actual: null, started: daysFromNow(-12), status: "At risk", risk: "medium" },
];

/* ---------- Support analytics (reports.support) ---------- */

// Tickets by client, within product: a client on two products has a row per product.
export const ticketsByCustomer = [
  { customer_id: 3, product_id: 1, open: 7, total: 31, breached: 4, csat: 3.1 },
  { customer_id: 1, product_id: 1, open: 4, total: 26, breached: 1, csat: 4.2 },
  { customer_id: 6, product_id: 1, open: 5, total: 22, breached: 3, csat: 3.4 },
  { customer_id: 18, product_id: 1, open: 3, total: 19, breached: 0, csat: 4.5 },
  { customer_id: 4, product_id: 1, open: 4, total: 17, breached: 2, csat: 3.6 },
  { customer_id: 5, product_id: 1, open: 3, total: 14, breached: 1, csat: 3.9 },
  { customer_id: 7, product_id: 1, open: 6, total: 12, breached: 1, csat: 3.8 },
  { customer_id: 10, product_id: 1, open: 1, total: 9, breached: 0, csat: 4.4 },
  { customer_id: 2, product_id: 1, open: 0, total: 3, breached: 0, csat: 4.8 },
  { customer_id: 19, product_id: 2, open: 4, total: 24, breached: 3, csat: 3.2 },
  { customer_id: 9, product_id: 2, open: 2, total: 15, breached: 0, csat: 4.6 },
  { customer_id: 2, product_id: 2, open: 3, total: 8, breached: 0, csat: 4.7 },
  { customer_id: 20, product_id: 2, open: 1, total: 2, breached: 0, csat: 4.5 },
  { customer_id: 9, product_id: 5, open: 2, total: 6, breached: 1, csat: 3.7 },
  { customer_id: 21, product_id: 4, open: 1, total: 5, breached: 0, csat: 4.6 },
];

// Support by product (and deployment type): volume, SLA and CSAT. Accounting is in an internal pilot only.
export const supportByProduct = [
  { product_id: 1, tickets: 153, open: 33, breached: 12, sla_met: 87.4, csat: 3.9, clients: 9, via_api: 48 },
  { product_id: 2, tickets: 49, open: 10, breached: 3, sla_met: 91.8, csat: 3.9, clients: 4, via_api: 31 },
  { product_id: 5, tickets: 6, open: 2, breached: 1, sla_met: 83.3, csat: 3.7, clients: 1, via_api: 4 },
  { product_id: 4, tickets: 5, open: 1, breached: 0, sla_met: 100, csat: 4.6, clients: 1, via_api: 5 },
  { product_id: 3, tickets: 0, open: 0, breached: 0, sla_met: null, csat: null, clients: 0, via_api: 0, note: "In development — internal pilot only, no client tickets yet" },
];

// The modules inside each product that generate the most tickets (from the "module" field the products send).
export const topModules = [
  { product_id: 1, module: "Payroll", tickets: 58, clients: 9, trend: "up" },
  { product_id: 1, module: "Statutory returns", tickets: 37, clients: 8, trend: "flat" },
  { product_id: 1, module: "Leave", tickets: 24, clients: 6, trend: "down" },
  { product_id: 1, module: "Employee self-service", tickets: 19, clients: 5, trend: "flat" },
  { product_id: 2, module: "Repayments / M-Pesa STK", tickets: 17, clients: 3, trend: "up" },
  { product_id: 2, module: "Interest & penalties", tickets: 11, clients: 3, trend: "flat" },
  { product_id: 2, module: "Loan disbursement", tickets: 9, clients: 3, trend: "up" },
  { product_id: 2, module: "Reports", tickets: 6, clients: 2, trend: "down" },
  { product_id: 5, module: "Leave", tickets: 4, clients: 1, trend: "up" },
  { product_id: 4, module: "Grower payments", tickets: 3, clients: 1, trend: "flat" },
];

// Channel: where the ticket came in; API tickets carry the product the client's connection is for.
export const ticketsByChannel = [
  { channel: "Portal", count: 61 },
  { channel: "Email", count: 48 },
  { channel: "Phone", count: 27 },
  { channel: "API · DeeHr360 HRM", count: 48 },
  { channel: "API · Demulla LMS", count: 31 },
  { channel: "API · standalone apps", count: 9 },
];

export const ticketCategories = ["Payroll / loans core", "Statutory & interest rules", "Access / login", "Integration (M-Pesa, bank, iTax)", "Reports", "Data correction", "Billing"];

// product_id × category counts.
export const ticketsByProductCategory = [
  { product_id: 1, counts: [58, 37, 11, 9, 8, 6, 4] },
  { product_id: 2, counts: [9, 11, 3, 17, 6, 2, 1] },
  { product_id: 5, counts: [2, 1, 0, 1, 1, 1, 0] },
  { product_id: 4, counts: [1, 0, 0, 3, 0, 1, 0] },
];

export const ticketTrend = {
  weeks: ["W32", "W33", "W34", "W35", "W36", "W37", "W38", "W39", "W40", "W41", "W42", "W43"],
  created: [12, 14, 11, 16, 15, 13, 18, 17, 14, 19, 21, 16],
  resolved: [11, 13, 12, 14, 15, 12, 15, 16, 15, 16, 18, 15],
};

// Per priority: SLA targets come from Settings → SLA Policies.
export const slaCompliance = [
  { priority: "critical", tickets: 9, response_met: 89, resolution_met: 67, avg_response_h: 0.8, avg_resolution_h: 5.1 },
  { priority: "high", tickets: 38, response_met: 92, resolution_met: 79, avg_response_h: 2.9, avg_resolution_h: 11.4 },
  { priority: "medium", tickets: 87, response_met: 95, resolution_met: 88, avg_response_h: 6.2, avg_resolution_h: 29.8 },
  { priority: "low", tickets: 50, response_met: 98, resolution_met: 94, avg_response_h: 11.0, avg_resolution_h: 61.0 },
];

export const backlogAgeing = [
  { bucket: "< 1 day", count: 9 },
  { bucket: "1–3 days", count: 12 },
  { bucket: "4–7 days", count: 8 },
  { bucket: "8–14 days", count: 5 },
  { bucket: "> 14 days", count: 4 },
];

export const recurringIssues = [
  { issue: "Payslips not emailing after payroll close", category: "Payroll", product_id: 1, count: 14, customers: 6, trend: "up" },
  { issue: "KRA iTax P10 upload rejected", category: "Statutory returns", product_id: 1, count: 11, customers: 7, trend: "flat" },
  { issue: "STK repayment callbacks not posting to loans", category: "M-Pesa / STK integration", product_id: 2, count: 9, customers: 3, trend: "up" },
  { issue: "Penalty raised on loans repaid before due date", category: "Interest & penalties", product_id: 2, count: 6, customers: 2, trend: "flat" },
  { issue: "Password reset links expire too fast", category: "Access / login", product_id: null, count: 7, customers: 5, trend: "flat" },
];

export const complaintRootCauses = [
  { cause: "Product defect", count: 9 },
  { cause: "Slow response", count: 6 },
  { cause: "Training gap", count: 5 },
  { cause: "Billing error", count: 3 },
  { cause: "Implementation quality", count: 3 },
  { cause: "Communication", count: 2 },
];

/* ---------- KPI catalogue (reports.kpis) ---------- */

const K = (key, name, formula, source, owner_id, frequency, target, actual, unit, direction = "up", tier = "MVP") => ({ key, name, formula, source, owner_id, frequency, target, actual, unit, direction, tier });

export const kpis = [
  K("prospect_lead_conv", "Prospect → lead conversion", "count(prospects converted) / count(prospects created) × 100", "Prospects", 2, "Monthly", 50, 49.2, "%"),
  K("lead_opp_conv", "Lead → opportunity conversion", "count(leads converted) / count(leads created) × 100", "Leads", 2, "Monthly", 55, 54.2, "%"),
  K("win_rate", "Win rate", "count(opps won) / count(opps closed) × 100", "Opportunities", 2, "Monthly", 40, 36.7, "%"),
  K("avg_deal", "Average deal size", "sum(won value) / count(opps won)", "Opportunities", 2, "Monthly", 850000, 841000, "KES"),
  K("cycle", "Sales cycle length", "avg(won_at − created_at) in days", "Opportunities", 2, "Monthly", 50, 52, "days", "down"),
  K("coverage", "Pipeline coverage", "sum(open pipeline) / remaining target", "Opportunities, Targets", 2, "Weekly", 3, 3.0, "×", "up", "Advanced"),
  K("velocity", "Sales velocity", "(open opps × win rate × avg deal) / cycle days", "Opportunities", 2, "Monthly", 250000, 231000, "KES/day", "up", "Advanced"),
  K("overdue_followups", "Overdue follow-ups", "count(tasks open and due < today)", "Tasks", 1, "Daily", 10, 17, "tasks", "down"),
  K("ttgl", "Time to go-live", "avg(actual go-live − contract signed) in days", "Onboarding", 6, "Monthly", 45, 46, "days", "down", "Phase 2"),
  K("sla_resolution", "SLA resolution compliance", "tickets resolved within SLA / tickets resolved × 100", "Tickets", 8, "Weekly", 90, 85.3, "%"),
  K("reopen", "Ticket reopen rate", "tickets reopened / tickets resolved × 100", "Tickets", 8, "Monthly", 5, 6.8, "%", "down"),
  K("csat", "CSAT", "avg(survey score) / 5 × 100", "Surveys", 7, "Monthly", 85, 79, "%", "up", "Phase 2"),
  K("nps", "NPS", "% promoters − % detractors", "Surveys", 7, "Quarterly", 35, 26, "pts", "up", "Phase 2"),
  K("renewal_rate", "Renewal rate", "renewed / due for renewal × 100", "Renewals", 7, "Monthly", 92, 88.9, "%"),
  K("churn_rate", "Logo churn rate", "customers lost / customers at start × 100", "Customers", 7, "Quarterly", 5, 5.9, "%", "down"),
  K("nrr", "Net revenue retention", "(start ARR + expansion − contraction − churn) / start ARR × 100", "Subscriptions", 11, "Quarterly", 105, 102.4, "%", "up", "Advanced"),
  K("rev_at_risk", "Revenue at risk", "sum(ARR of customers with risk ≥ At risk)", "Customers, Health", 7, "Weekly", 3000000, 5400000, "KES", "down", "Advanced"),
];

export const kpiChangeLog = [
  { at: `${daysFromNow(-12)}T10:14:00`, kpi: "win_rate", field: "formula", old: "count(opps won) / count(opps created) × 100", new: "count(opps won) / count(opps closed) × 100", by: "Brian Otieno", reason: "Open deals should not count against win rate.", effective: daysFromNow(-11), approval: "APR-00488" },
  { at: `${daysFromNow(-30)}T16:02:00`, kpi: "sla_resolution", field: "target", old: "95", new: "90", by: "Faith Achieng", reason: "Aligned to the new SLA policy for low priority.", effective: daysFromNow(-29), approval: "APR-00451" },
  { at: `${daysFromNow(-44)}T09:30:00`, kpi: "rev_at_risk", field: "formula", old: "sum(MRR of at-risk customers)", new: "sum(ARR of customers with risk ≥ At risk)", by: "Grace Njeri", reason: "Management reports in annual value.", effective: daysFromNow(-44), approval: "APR-00433" },
];

// Month-end snapshots (P8.29). A locked period cannot be recalculated.
export const snapshots = [
  { period: "Sep 2026", locked: true, locked_by: "Mercy Wambui", locked_at: `${daysFromNow(-15)}T18:00:00`, values: { prospect_lead_conv: 47.5, win_rate: 34.8, avg_deal: 802000, sla_resolution: 87.1, churn_rate: 5.2, rev_at_risk: 4900000 } },
  { period: "Aug 2026", locked: true, locked_by: "Mercy Wambui", locked_at: `${daysFromNow(-46)}T18:00:00`, values: { prospect_lead_conv: 51.0, win_rate: 38.9, avg_deal: 760000, sla_resolution: 89.4, churn_rate: 4.6, rev_at_risk: 4100000 } },
  { period: "Jul 2026", locked: true, locked_by: "Mercy Wambui", locked_at: `${daysFromNow(-77)}T18:00:00`, values: { prospect_lead_conv: 44.2, win_rate: 28.6, avg_deal: 725000, sla_resolution: 90.8, churn_rate: 4.1, rev_at_risk: 3600000 } },
  { period: "Oct 2026", locked: false, locked_by: null, locked_at: null, values: { prospect_lead_conv: 49.2, win_rate: 36.7, avg_deal: 841000, sla_resolution: 85.3, churn_rate: 5.9, rev_at_risk: 5400000 } },
];

